/**
 * Hunt Journal Service — business logic for append-only journal entries.
 *
 * Enforces:
 *  - Body length 1–280 characters
 *  - visibility always "shared" (v1 — no per-entry privacy toggle)
 *
 * Called by hunt-journal-store.ts. Never imported by components.
 */

import { db } from '@/services/storage/db';
import { useAuthStore } from '@/stores/auth-store';
import type { HuntJournalEntry, CreateJournalEntryInput } from '@/types/hunt-journal-entry';

const MAX_BODY_LENGTH = 280;

/**
 * Create a new journal entry for a hunt.
 * Throws if body is empty or exceeds 280 characters.
 */
export async function createJournalEntry(
  input: Omit<CreateJournalEntryInput, 'userId'>,
): Promise<HuntJournalEntry> {
  const trimmed = input.body.trim();
  if (trimmed.length === 0) {
    throw new Error('Journal entry body cannot be empty');
  }
  if (trimmed.length > MAX_BODY_LENGTH) {
    throw new Error(`Body exceeds ${MAX_BODY_LENGTH} characters`);
  }

  const userId = useAuthStore.getState().user?.id;
  if (!userId) {
    throw new Error('[hunt-journal-service]: cannot create entry — user not authenticated');
  }
  const now = new Date().toISOString();

  const entry: HuntJournalEntry = {
    id: crypto.randomUUID(),
    huntId: input.huntId,
    userId,
    body: trimmed,
    // Enforce shared visibility — no per-entry privacy toggle in v1
    visibility: 'shared',
    tag: input.tag,
    locationId: input.locationId,
    huntSitId: input.huntSitId,
    createdAt: now,
    updatedAt: now,
  };

  return db.createHuntJournalEntry(entry);
}

/**
 * Return all active (non-deleted) journal entries for a hunt,
 * sorted by createdAt ascending (oldest first).
 */
export async function getJournalEntriesByHunt(huntId: string): Promise<HuntJournalEntry[]> {
  const entries = await db.getHuntJournalEntriesByHunt(huntId);
  // Filter out soft-deleted entries in case the adapter returns them
  return entries
    .filter((e) => !e.deletedAt)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}
